/* M6.3 — COD Dashboard. KpiStrip + settlement-status donut + COD ageing by
   courier + reconciliation-by-courier gauges, all respecting the courier +
   delivered-window filters. Clicking the COD Due / Overdue / Received cards
   seeds the Workspace status filter and switches tab. Includes the weekly-digest
   preview modal and the live "refreshed Ns ago" chip. */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {KpiStrip, Modal, SearchSelect, useToast, inr, money} from '../components'
import {Donut, AgeBar, AgeLegend, BUCKETS, VerticalChip, CountValue} from './codBits'
import {
  visibleCodRows,
  visibleDisputes,
  visibleVerticals,
  codStatus,
  overdueBucket,
  TODAY,
  CURRENT_USER,
} from './codLogic'
import {TODAY as PINNED} from '../dummyData/format'

const STATUS_COLORS = {
  Received: '#2E7D32',
  Due: '#1976D2',
  Overdue: '#C62828',
  Disputed: '#B45309',
  Reversed: '#6B7280',
}
const ic = {w: 14, h: 14}

export default function DashboardTab({onSeedWorkspace}) {
  const toast = useToast()
  const [courier, setCourier] = useState('All')
  const [days, setDays] = useState('All')
  const [digest, setDigest] = useState(null)
  const [secs, setSecs] = useState(0)
  const lastRefresh = useRef(Date.now())

  const couriers = useMemo(() => {
    const set = []
    visibleCodRows().forEach((r) => {
      if (set.indexOf(r.courier) < 0) set.push(r.courier)
    })
    return set.sort()
  }, [])

  const withinWindow = (dateStr) => {
    if (days === 'All') return true
    const d = new Date(dateStr)
    return (TODAY - d) / 86400000 <= +days
  }

  const rows = useMemo(() => {
    return visibleCodRows().filter((r) => {
      if (courier !== 'All' && r.courier !== courier) return false
      return withinWindow(r.delivered)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courier, days])

  /* live "refreshed Ns ago" ticker */
  useEffect(() => {
    lastRefresh.current = Date.now()
    setSecs(0)
    const t = setInterval(() => setSecs(Math.round((Date.now() - lastRefresh.current) / 1000)), 1000)
    return () => clearInterval(t)
  }, [courier, days])

  /* ── aggregates ── */
  const sc = {Received: 0, Due: 0, Overdue: 0, Disputed: 0, Reversed: 0}
  rows.forEach((r) => {
    const s = codStatus(r)
    if (sc[s] != null) sc[s]++
  })
  const totalDue = rows.filter((r) => r.due).reduce((s, r) => s + r.codValue, 0)
  const overdueAmt = rows.filter((r) => codStatus(r) === 'Overdue').reduce((s, r) => s + r.codValue, 0)
  const receivedRows = rows.filter(
    (r) => r.courierStatus === 'Received' && r.receivedOn && withinWindow(r.receivedOn)
  )
  const received = receivedRows.reduce((s, r) => s + r.codValue, 0)
  const reconDenom = sc.Received + sc.Due + sc.Overdue
  const reconRate = reconDenom ? Math.round((sc.Received * 100) / reconDenom) : 0
  const disp = visibleDisputes().filter((d) => d.status !== 'Resolved' && d.status !== 'Rejected')
  const dispAtRisk = disp.reduce((s, d) => s + d.codValue, 0)
  const reversals = visibleDisputes().filter((d) => d.status === 'Reversal Generated')
  const revAmt = reversals.reduce((s, d) => s + d.codValue, 0)

  const mism = rows.filter((r) => r.statusMatch === 'Mismatch')
  const mismAmt = mism.reduce((s, r) => s + r.codValue, 0)

  /* de-alarm: zero problem-count is not a warning (grey, not red) */
  const kpiCards = [
    {
      label: 'Total COD Due',
      value: <CountValue value={totalDue} money />,
      icon: <MonetizationOnOutlinedIcon style={ic} />,
      accent: '#1976D2',
      sub: 'Courier still owes Prozo',
      onClick: () => onSeedWorkspace(['Due', 'Overdue'], courier),
    },
    {
      label: 'Overdue COD',
      value: <CountValue value={overdueAmt} money />,
      icon: <WarningAmberOutlinedIcon style={ic} />,
      accent: overdueAmt ? '#C62828' : '#6B7280',
      sub: 'Past expected remittance',
      onClick: () => onSeedWorkspace(['Overdue'], courier),
    },
    {
      label: 'Received this period',
      value: <CountValue value={received} money />,
      icon: <CheckCircleOutlineIcon style={ic} />,
      accent: '#2E7D32',
      sub: receivedRows.length + ' AWBs settled',
      onClick: () => onSeedWorkspace(['Received'], courier),
    },
    {
      label: 'Reconciliation rate',
      value: <CountValue value={reconRate} suffix='%' />,
      icon: <SyncOutlinedIcon style={ic} />,
      accent: '#6A1B9A',
      sub: sc.Received + ' of ' + reconDenom + ' (excl. disputed/reversed)',
    },
    {
      label: 'Open disputes',
      value: <CountValue value={disp.length} />,
      icon: <AssignmentLateOutlinedIcon style={ic} />,
      accent: disp.length ? '#B45309' : '#6B7280',
      sub: money(dispAtRisk) + ' at risk',
      onClick: () => onSeedWorkspace(null, courier, 'disputes'),
    },
    {
      label: 'Reversals this period',
      value: <CountValue value={reversals.length} />,
      icon: <HistoryOutlinedIcon style={ic} />,
      accent: '#6B7280',
      sub: money(revAmt) + ' clawed back',
    },
  ]

  const donutSegs = [
    {label: 'Received', value: sc.Received, color: STATUS_COLORS.Received},
    {label: 'Due (not yet overdue)', value: sc.Due, color: STATUS_COLORS.Due},
    {label: 'Overdue', value: sc.Overdue, color: STATUS_COLORS.Overdue},
    {label: 'Disputed', value: sc.Disputed, color: STATUS_COLORS.Disputed},
    {label: 'Reversed', value: sc.Reversed, color: STATUS_COLORS.Reversed},
  ]

  /* ageing per courier */
  const byC = {}
  rows.forEach((r) => {
    const b = overdueBucket(r)
    if (b == null) return
    const o =
      byC[r.courier] ||
      (byC[r.courier] = {
        courier: r.courier,
        vertical: r.vertical,
        notdue: 0,
        '0-3': 0,
        '4-7': 0,
        '8-15': 0,
        '15+': 0,
        amt: 0,
      })
    o[b] += 1
    if (b !== 'notdue') o.amt += r.codValue
  })
  const ageList = Object.keys(byC)
    .map((k) => byC[k])
    .sort((a, b) => b.amt - a.amt)
  const ageMax = Math.max.apply(
    null,
    ageList.map((o) => BUCKETS.reduce((s, b) => s + (o[b[0]] || 0), 0)).concat([1])
  )

  /* reconciliation rate by courier */
  const rec = {}
  rows.forEach((r) => {
    const o =
      rec[r.courier] || (rec[r.courier] = {courier: r.courier, vertical: r.vertical, total: 0, recv: 0, due: 0})
    o.total++
    if (r.courierStatus === 'Received') o.recv++
    else o.due += r.codValue
  })
  const recList = Object.keys(rec)
    .map((k) => rec[k])
    .sort((a, b) => b.recv / b.total - a.recv / a.total)

  return (
    <div>
      <div className='filtbar'>
        <div className='seg'>
          <label>Courier</label>
          <div style={{minWidth: 180}}>
            <SearchSelect
              value={courier}
              placeholder='All couriers'
              options={[{value: 'All', label: 'All couriers'}, ...couriers.map((c) => ({value: c, label: c}))]}
              onChange={(v) => setCourier(v || 'All')}
            />
          </div>
        </div>
        <div className='seg'>
          <label>Window</label>
          <div style={{minWidth: 170}}>
            <SearchSelect
              value={days}
              placeholder='All time'
              options={[
                {value: 'All', label: 'All time'},
                {value: '7', label: 'Delivered ≤ 7 days'},
                {value: '30', label: 'Delivered ≤ 30 days'},
                {value: '90', label: 'Delivered ≤ 90 days'},
              ]}
              onChange={(v) => setDays(v || 'All')}
            />
          </div>
        </div>
        <span>
          <span className='live-chip'>
            <SyncOutlinedIcon style={{width: 12, height: 12}} />
            Live · refreshed {secs}s ago
          </span>
        </span>
        <span className='hlp' style={{marginLeft: 4}}>
          Courier and delivery-window filters apply to every widget below.
        </span>
        <button className='btn btn-sm' style={{marginLeft: 'auto'}} onClick={() => setDigest(true)}>
          <MailOutlineIcon style={{width: 15, height: 15}} />
          Preview weekly digest
        </button>
      </div>

      {mism.length ? (
        <div className='warnbox err' style={{margin: '0 0 18px', alignItems: 'center'}}>
          <span style={{display: 'flex'}}>
            <ErrorOutlineIcon style={{width: 16, height: 16}} />
          </span>
          <div style={{flex: 1}}>
            {mism.length} AWBs have a courier ↔ ProShip status mismatch ({money(mismAmt)} at risk).
          </div>
          <button className='btn btn-sm' onClick={() => onSeedWorkspace(null, courier, 'disputes')}>
            <ArrowForwardIcon style={{width: 14, height: 14}} />
            Review in Disputes
          </button>
        </div>
      ) : null}

      <KpiStrip cards={kpiCards} />

      <div className='chart-2'>
        <div className='card'>
          <div className='card-hd'>
            <h3>Settlement Status</h3>
            <span className='muted' style={{fontSize: 12}}>
              {rows.length} delivered AWBs
            </span>
          </div>
          <div className='card-pad'>
            <Donut segs={donutSegs} unit='AWBs' />
          </div>
        </div>
        <div className='card'>
          <div className='card-hd'>
            <h3>COD Ageing by Courier</h3>
            <span className='muted' style={{fontSize: 12}}>
              pending AWBs by days past expected remittance
            </span>
          </div>
          <div className='card-pad'>
            {ageList.length ? (
              <>
                {ageList.map((o) => {
                  const rowTot = BUCKETS.reduce((s, b) => s + (o[b[0]] || 0), 0)
                  const pendN = rowTot - (o.notdue || 0)
                  return (
                    <div className='barrow' key={o.courier}>
                      <div>
                        <b>{o.courier}</b> <VerticalChip vertical={o.vertical} />
                      </div>
                      <AgeBar o={o} maxTotal={ageMax} />
                      <div style={{textAlign: 'right', fontWeight: 700, color: o.amt ? 'var(--error)' : undefined}}>
                        {o.amt ? inr(o.amt) : <span className='muted'>—</span>}
                        <div className='muted' style={{fontSize: 11, fontWeight: 500}}>
                          {pendN} pending AWB{pendN !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  )
                })}
                <AgeLegend />
              </>
            ) : (
              <div className='empty' style={{padding: 24}}>
                <div className='ic'>
                  <CheckCircleOutlineIcon style={{width: 26, height: 26}} />
                </div>
                <h3>Nothing pending</h3>
                <p>No pending COD across visible couriers.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='card' style={{marginTop: 16}}>
        <div className='card-hd'>
          <h3>Reconciliation by Courier</h3>
          <span className='muted' style={{fontSize: 12}}>
            % of delivered AWBs settled · COD still due
          </span>
        </div>
        <div className='card-pad'>
          {recList.map((o) => {
            const pct = Math.round((o.recv * 100) / o.total)
            const col = pct >= 80 ? '#2E7D32' : pct >= 50 ? '#B45309' : '#C62828'
            return (
              <div
                className='barrow'
                key={o.courier}
                style={{gridTemplateColumns: '160px 1fr 56px 120px'}}
              >
                <div>
                  <b>{o.courier}</b> <VerticalChip vertical={o.vertical} />
                </div>
                <div
                  className='pbar pbar-lg'
                  title={`${o.courier} — ${pct}% settled (${o.recv}/${o.total} AWBs)${
                    o.due ? ' · ' + inr(o.due) + ' still due' : ''
                  }`}
                >
                  <Bar pct={pct} color={col} />
                </div>
                <div style={{fontWeight: 700, color: col, textAlign: 'right'}}>{pct}%</div>
                <div style={{textAlign: 'right'}} className={o.due ? undefined : 'muted'}>
                  {o.due ? inr(o.due) + ' due' : 'all settled'}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div
        className='card card-pad'
        style={{
          marginTop: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          color: 'var(--neutral)',
          fontSize: 13,
        }}
      >
        <InfoOutlinedIcon style={{width: 18, height: 18, flexShrink: 0, marginTop: 2}} />
        <span>
          Cards respect the courier and delivery-window filters above. Clicking the COD Due / Overdue
          / Received cards opens the Reconciliation Workspace with the matching status filter
          pre-applied. Reconciliation rate counts Received over (Received + Due + Overdue), excluding
          disputed and reversed AWBs. Overdue judgement uses M6 Cycle Config — not the legacy Courier
          Due Date.
        </span>
      </div>

      <DigestModal
        open={!!digest}
        onClose={() => setDigest(null)}
        rows={rows}
        courier={courier}
        toast={toast}
      />
    </div>
  )
}
DashboardTab.propTypes = {onSeedWorkspace: PropTypes.func}

/* animated pbar fill */
function Bar({pct, color}) {
  const ref = useRef(null)
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (ref.current) ref.current.style.width = pct + '%'
    })
    return () => cancelAnimationFrame(id)
  }, [pct])
  return <span ref={ref} style={{background: color, width: 0}} />
}
Bar.propTypes = {pct: PropTypes.number, color: PropTypes.string}

/* M6.19b — weekly digest preview */
function DigestModal({open, onClose, rows, courier, toast}) {
  const sc = {Received: 0, Due: 0, Overdue: 0}
  rows.forEach((r) => {
    const s = codStatus(r)
    if (sc[s] != null) sc[s]++
  })
  const reconDenom = sc.Received + sc.Due + sc.Overdue
  const reconRate = reconDenom ? Math.round((sc.Received * 100) / reconDenom) : 0
  const byC = {}
  rows.forEach((r) => {
    if (codStatus(r) !== 'Overdue') return
    const o = byC[r.courier] || (byC[r.courier] = {courier: r.courier, amt: 0, n: 0})
    o.amt += r.codValue
    o.n++
  })
  const top = Object.keys(byC)
    .map((k) => byC[k])
    .sort((a, b) => b.amt - a.amt)
    .slice(0, 5)
  const overdueTotal = top.reduce((s, o) => s + o.amt, 0)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Weekly digest preview'
      icon={<MailOutlineIcon fontSize='small' />}
      width={520}
      actions={[
        {label: 'Close', onClick: onClose},
        {
          label: 'Send test digest',
          primary: true,
          icon: <MailOutlineIcon fontSize='small' />,
          onClick: () => toast('Test digest sent to recipients (preview).', 'success'),
        },
      ]}
    >
      <p style={{fontSize: 13, margin: '0 0 12px'}}>
        Weekly COD reconciliation digest for <b>{visibleVerticals().join(' + ')}</b>
        {courier !== 'All' ? ' · ' + courier : ''}.
      </p>
      <div className='dl' style={{gridTemplateColumns: 'auto 1fr', maxWidth: 460, marginBottom: 14}}>
        <dt>Reconciliation rate</dt>
        <dd>
          <b>{reconRate}%</b> ({sc.Received} of {reconDenom})
        </dd>
        <dt>Overdue (top couriers)</dt>
        <dd>
          <b style={{color: 'var(--error)'}}>{money(overdueTotal)}</b>
        </dd>
      </div>
      <div className='seclabel' style={{margin: '6px 0 8px'}}>
        Top overdue couriers
      </div>
      {top.length ? (
        <div className='tbl-wrap'>
          <table className='tbl'>
            <thead>
              <tr>
                <th>Courier</th>
                <th style={{textAlign: 'right'}}>Overdue ₹</th>
                <th style={{textAlign: 'right'}}>AWBs</th>
              </tr>
            </thead>
            <tbody>
              {top.map((o) => (
                <tr key={o.courier}>
                  <td>
                    <b>{o.courier}</b>
                  </td>
                  <td style={{textAlign: 'right'}}>
                    <span className='mono'>{inr(o.amt)}</span>
                  </td>
                  <td style={{textAlign: 'right'}}>{o.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className='muted' style={{fontSize: 13}}>
          No overdue COD in the current scope — nothing to chase this week.
        </div>
      )}
    </Modal>
  )
}
DigestModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  rows: PropTypes.array,
  courier: PropTypes.string,
  toast: PropTypes.func,
}

// keep pinned-today reference parity with the logic module
export {PINNED, CURRENT_USER}
